import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import './SettingsPage.css';
import './AdminOnboardPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const EyeIcon: React.FC<{ hidden: boolean }> = ({ hidden }) => (
  hidden ? (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 5c-5.5 0-9.5 5.2-9.9 5.7a1 1 0 0 0 0 1.1C2.5 12.3 6.5 18 12 18s9.5-5.7 9.9-6.2a1 1 0 0 0 0-1.1C21.5 10.2 17.5 5 12 5Zm0 11a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-2.5A2.5 2.5 0 1 0 12 8.5a2.5 2.5 0 0 0 0 5Z"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M3.3 2.3a1 1 0 0 0-1.4 1.4l3.1 3.1C3.2 8.3 1.6 10.3 1.1 11a1 1 0 0 0 0 1.1C1.5 12.7 5.5 18 12 18c2.1 0 3.9-.5 5.4-1.3l3.3 3.3a1 1 0 1 0 1.4-1.4L3.3 2.3ZM12 16c-4.4 0-7.6-3.6-8.8-5 .6-.8 1.8-2.1 3.4-3.1l2.1 2.1A5 5 0 0 0 12 16Zm7.6-2.2c-.4.5-.9 1-1.5 1.5l-1.5-1.5c.6-.7.9-1.5.9-2.3A5 5 0 0 0 12 6.5c-.4 0-.8 0-1.1.1L9.3 5C10.1 4.7 11 4.5 12 4.5c5.5 0 9.5 5.2 9.9 5.7a1 1 0 0 1 0 1.1c-.5.6-1.2 1.5-2.3 2.5Z"
      />
    </svg>
  )
);

const AdminOnboardPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [completed, setCompleted] = useState(false);
  const [done, setDone] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setLoadError('This onboarding link is invalid.');
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_BASE_URL}/admin/onboard/${encodeURIComponent(token)}`);
        const data = await response.json();
        if (!response.ok || !data.success) {
          setLoadError(data.message || 'This onboarding link is invalid or has expired.');
          setLoading(false);
          return;
        }
        setName(data.name || '');
        setEmail(data.email || '');
        setCompleted(!!data.completed);
      } catch {
        setLoadError('Failed to connect to server');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }
    if (!accepted) {
      setSubmitError('You must accept the admin access waiver');
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/onboard/${encodeURIComponent(token || '')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          accessWaiverAccepted: true,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setSubmitError(data.message || 'Failed to complete onboarding');
        return;
      }
      setDone(true);
    } catch {
      setSubmitError('Failed to connect to server');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-card onboard-card">
        <div className="settings-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="settings-title">Admin Access Setup</h1>
          <p className="settings-subtitle">
            {name ? `Welcome, ${name}. ` : ''}
            Set your password and accept the admin access waiver to use the admin panel.
          </p>
        </div>

        {loading && <p>Loading your unique setup page...</p>}

        {!loading && loadError && !done && (
          <div className="access-error">{loadError}</div>
        )}

        {!loading && (completed || done) && (
          <div className="access-banner">
            {done
              ? 'Onboarding complete. You can now sign in with your new password.'
              : 'Onboarding is already complete. You can sign in on the admin login page.'}
          </div>
        )}

        {!loading && !completed && !done && !loadError && (
          <form onSubmit={handleSubmit} className="access-form onboard-form">
            {email && <p className="access-help onboard-account">Account: {email}</p>}

            <section className="onboard-waiver">
              <h2>Admin Access Waiver</h2>
              <p>
                You are being granted administrator access to the FabMo Volunteer App. Admins can view
                volunteer records, manage access, and receive building access information, including the
                building code.
              </p>
              <p>By accepting, you agree that:</p>
              <ul>
                <li>You will use admin access only for FabMo volunteer operations.</li>
                <li>You will keep the building code and any other access information confidential.</li>
                <li>You will not share your admin login with anyone else.</li>
                <li>You are responsible for actions taken with your account.</li>
              </ul>
              <label className="onboard-check">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  disabled={submitting}
                  required
                />
                <span>
                  <span className="required-asterisk">*</span> I understand I have admin rights to this website and will have access to the building code. I accept this responsibility.
                </span>
              </label>
            </section>

            <label className="onboard-field">
              <span>
                New password <span className="required-asterisk">*</span>
              </span>
              <div className="password-field">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={submitting}
                >
                  <EyeIcon hidden={!showPassword} />
                </button>
              </div>
            </label>
            <label className="onboard-field">
              <span>
                Confirm password <span className="required-asterisk">*</span>
              </span>
              <div className="password-field">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={submitting}
                  minLength={8}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  disabled={submitting}
                >
                  <EyeIcon hidden={!showConfirm} />
                </button>
              </div>
            </label>

            {submitError && <div className="access-error">{submitError}</div>}

            <button
              type="submit"
              className="access-primary-btn onboard-submit"
              disabled={submitting || !accepted}
            >
              {submitting ? 'Saving...' : 'Set password and accept'}
            </button>
          </form>
        )}

        <div className="settings-footer onboard-footer">
          <Link to="/admin-login" className="back-button onboard-login-link">
            Go to Admin Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminOnboardPage;
